import React, { useContext, useEffect, useState } from 'react';
import { LoginContext } from '../../../App';
import { updateObjectFieldsWithLabels } from '../../b2b-languages/components/helper';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import { Add, Delete, Save, Straighten } from '@mui/icons-material';
import LabelTranslated from '../../b2b-languages/components/LabelTranslated';
import CategoriesSelector from '../components/CategoriesSelector';
import { CategoriesChips, ConvertFromTable, ConvertToTable, getMeasurementUnit } from '../components/helper';

import './MeasurementUnitModal.css';
import { deleteMeasurementUnit, saveNewMeasurementUnit, updateMeasurementUnit } from '../../../services/b2b-indicators-apiService';
import B2BPaper from '../../b2b-common/B2BPaper';
import ConversionModal from './ConversionModal';

const MeasurementUnitModal = ({ open, toChangeUnitId = null, onUpdate, onClose }) => {

    const { loginData } = useContext(LoginContext);
    const emptyMeasurementUnit = {
        "quantitativeMeasurementUnitId": null,
        "quantitativeMeasurementUnit": null,
        "quantitativeMeasurementUnit_label": null,
        "quantitativeMeasurementUnitName": null,
        "quantitativeMeasurementUnitName_label": null,
        "quantitativeMeasurementUnitDescription": null,
        "quantitativeMeasurementUnitDescription_label": null,
        "convertFromUnits": [],
        "convertToUnits": [],
        "categories": [],
    };
    const [unit, setUnit] = useState(emptyMeasurementUnit);
    const [error, setError] = useState(null);

    useEffect(() => {
        // console.log('toChangeUnitId:', toChangeUnitId);
        if (toChangeUnitId) {
            try {
                getMeasurementUnit(loginData, toChangeUnitId, setUnit, setError);
            } catch (error) {
                console.error('Error getting measurement unit:', error);
            }
        }
        else setUnit(emptyMeasurementUnit);
    }, [toChangeUnitId]);

    const handleSaveNewUnit = async () => {
        // console.log('Saving new unit:', unit);
        try {
            const updatedUnit = updateObjectFieldsWithLabels(unit);
            await saveNewMeasurementUnit(unit, loginData.accessToken);
            onUpdate();
        } catch (error) {
            console.error('Error saving new unit:', error);
        }
    }

    const handleUpdateUnit = async () => {
        // console.log('Updating unit:', unit);
        try {
            const updatedUnit = updateObjectFieldsWithLabels(unit);
            // console.log('Updated unit:', updatedUnit);
            await updateMeasurementUnit(updatedUnit, loginData.accessToken);
            onUpdate();
        } catch (error) {
            console.error('Error updating unit:', error);
        }
    }

    const handleDeleteUnit = async () => {
        console.log('Deleting unit:', unit);
        try {
            await deleteMeasurementUnit(unit, loginData.accessToken);
            onUpdate();
        } catch (error) {
            console.error('Error deleting unit:', error);
        }
    }

    const handleSelectedCategory = (category) => {
        setUnit(prevUnit => {
            // Ensure categories is always an array
            const categories = prevUnit.categories || [];
            if (!categories.includes(category)) {
                const updatedCategories = [...categories, category];
                return { ...prevUnit, categories: updatedCategories };
            }
            return prevUnit;
        });
    }

    const handleRemoveCategory = (category) => {
        // console.log('Removing category:', category);
        setUnit(prevUnit => {
            const updatedCategories = prevUnit.categories.filter(cat => cat.measurementUnitCategoryId !== category.measurementUnitCategoryId);
            //console.log('Updated categories:', updatedCategories);
            return { ...prevUnit, categories: updatedCategories };
        });
    }

    const [openConversionModal, setOpenConversionModal] = useState(false);
    const [selectedConversion, setSelectedConversion] = useState(null);

    const handleSelectConversion = (conversion) => {
        // console.log('Selected Conversion:', conversion);
        setSelectedConversion(conversion);
        setOpenConversionModal(true);
    }

    const handleUpdatedConversion = async () => {
        await getMeasurementUnit(loginData, unit.quantitativeMeasurementUnitId, setUnit, setError);
        setOpenConversionModal(false);
    }

    const buttonsNewUnit = [
        {
            label: 'Save new unit',
            icon: <Add />,
            onClick: handleSaveNewUnit,
        },
    ];

    const buttonsUpdateUnit = [
        {
            label: 'Update unit',
            icon: <Save />,
            onClick: handleUpdateUnit,
        },
        {
            label: 'Delete unit',
            icon: <Delete />,
            confirm: true,
            onConfirm: handleDeleteUnit,
        },
    ];

    return (
        <B2BPopUpForm
            open={open}
            icon={<Straighten />}
            title={toChangeUnitId ? 'Update unit' : 'New unit'}
            onClose={onClose}
            buttons={toChangeUnitId ? buttonsUpdateUnit : buttonsNewUnit}
            className="b2b-measurement-unit-modal"
        >
            <div className='measurement-unit'>
                <B2BPaper title='Categories' elevation={0} className='unit-categories'>
                    <CategoriesSelector onSelectedCategory={handleSelectedCategory} className='categories-selector' />
                    <CategoriesChips categories={unit.categories} onDeletedCategory={handleRemoveCategory} />
                </B2BPaper>
                <B2BPaper title='Fields' elevation={0} className='unit-fields'>
                    <LabelTranslated
                        field="Name" labelValue={unit.quantitativeMeasurementUnitName_label}
                        onChange={(e) => setUnit({ ...unit, quantitativeMeasurementUnitName_label: e.target.value })}
                        locale={`Name [${loginData.locale}]`} translation={unit.quantitativeMeasurementUnitName} />
                    <LabelTranslated
                        field="Unit" labelValue={unit.quantitativeMeasurementUnit_label}
                        onChange={(e) => setUnit({ ...unit, quantitativeMeasurementUnit_label: e.target.value })}
                        locale={`Unit [${loginData.locale}]`} translation={unit.quantitativeMeasurementUnit} />
                    <LabelTranslated
                        field="Description" labelValue={unit.quantitativeMeasurementUnitDescription_label}
                        onChange={(e) => setUnit({ ...unit, quantitativeMeasurementUnitDescription_label: e.target.value })}
                        locale={`Description [${loginData.locale}]`} translation={unit.quantitativeMeasurementUnitDescription}
                        rowsNumber={3}
                    />
                </B2BPaper>
                {unit.convertToUnits && unit.convertToUnits.length > 0 && (
                    <B2BPaper title='Converts to:' elevation={0} className='measurement-unit-convert-from'>
                        <ConvertToTable
                            convertToUnits={unit.convertToUnits}
                            onSelectConversion={handleSelectConversion}
                        />
                    </B2BPaper>
                )}
                {unit.convertFromUnits && unit.convertFromUnits.length > 0 && (
                    <B2BPaper title='Converts from:' elevation={0} className='measurement-unit-convert-to'>
                        <ConvertFromTable
                            convertFromUnits={unit.convertFromUnits}
                            onSelectConversion={handleSelectConversion}
                        />
                    </B2BPaper>
                )}
            </div>
            <ConversionModal
                open={openConversionModal}
                onClose={() => setOpenConversionModal(false)}
                toChangeConversion={selectedConversion}
                onUpdate={handleUpdatedConversion}
            />
        </B2BPopUpForm>

    );
};

export default MeasurementUnitModal;